import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import {
  PlugsConnected, GitBranch, ListChecks, CheckCircle, XCircle, ArrowUpRight,
} from "@phosphor-icons/react";

const Stat = ({ label, value, icon: Icon, accent }) => (
  <div data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}
    className="bg-white border border-stone-200 rounded-2xl shadow-card p-6 flex flex-col justify-between h-full">
    <div className="flex items-start justify-between">
      <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">{label}</span>
      <Icon size={20} className={accent ? "text-[#C2410C]" : "text-stone-400"} weight="regular" />
    </div>
    <div className="stat-num text-4xl font-bold text-stone-950 mt-6">{value}</div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);

  return (
    <Layout>
      <PageHeader title="Dashboard" subtitle="Migration platform overview" />
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Connections" value={data?.connections ?? "—"} icon={PlugsConnected} />
          <Stat label="Projects" value={data?.projects ?? "—"} icon={GitBranch} />
          <Stat label="Records Migrated" value={data?.records_migrated ?? "—"} icon={CheckCircle} accent />
          <Stat label="Records Failed" value={data?.records_failed ?? "—"} icon={XCircle} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Stat label="Total Jobs" value={data?.total_jobs ?? "—"} icon={ListChecks} />
          <Stat label="Running" value={data?.running_jobs ?? "—"} icon={ArrowUpRight} accent />
          <Stat label="Completed" value={data?.completed_jobs ?? "—"} icon={CheckCircle} />
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl shadow-card">
          <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
            <h3 className="font-head font-semibold text-stone-950">Recent Migration Jobs</h3>
            <Link to="/jobs" className="text-xs text-[#C2410C] hover:underline">View all</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                  <th className="px-6 py-3 font-semibold">Project</th>
                  <th className="px-6 py-3 font-semibold">Mode</th>
                  <th className="px-6 py-3 font-semibold">Status</th>
                  <th className="px-6 py-3 font-semibold text-right">Success</th>
                  <th className="px-6 py-3 font-semibold text-right">Failed</th>
                  <th className="px-6 py-3 font-semibold text-right">Progress</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent_jobs || []).map((j) => (
                  <tr key={j.id} className="border-b border-stone-100 hover:bg-stone-50 transition-colors duration-200">
                    <td className="px-6 py-3">
                      <Link to={`/jobs/${j.id}`} className="font-medium text-stone-900 hover:text-[#C2410C]">{j.project_name}</Link>
                    </td>
                    <td className="px-6 py-3 capitalize text-stone-600">{j.mode}</td>
                    <td className="px-6 py-3"><StatusBadge status={j.status} /></td>
                    <td className="px-6 py-3 text-right font-mono text-emerald-600">{j.successful}</td>
                    <td className="px-6 py-3 text-right font-mono text-rose-600">{j.failed}</td>
                    <td className="px-6 py-3 text-right font-mono">{j.progress_pct}%</td>
                  </tr>
                ))}
                {data && data.recent_jobs.length === 0 && (
                  <tr><td colSpan={6} className="px-6 py-10 text-center text-stone-400 text-sm">
                    No migration jobs yet. Create a project to get started.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
